import { Integer } from '@type-r/ext-types';
import { attributes, AttributesMixin, define, Model, type } from '@type-r/models';
import { asDate } from 'lqx-react/lib';
import { createdUpdatedAttrs, matchString } from 'api/common';
import { fetchJson, restfulIO } from 'api/endpoint';
import { localUserInfo } from 'api/localUserInfo';
import { root } from './common';
import { getAuthToken, getGeneralPlatformCode } from 'api/embedded-auth';
import { globalStore } from 'api';

export interface InvoiceInfo extends AttributesMixin<typeof InvoiceInfo> {}
@define
export class InvoiceInfo extends Model {
    static attributes = {
        invoiceNumber: String,
        dueDate : Date,
        outstandingAmount: Number,
        creditedAmount : type(Number).check(function(x) {return x <= this.outstandingAmount;}).watcher( 'onCreditedAmountChange'),
        paymentAmount : type(Number),
        currency : String,
        unfunded : Boolean,
        invCustomerName : String,
        invSupplierName : String,
        invoicedAmount: Number,
    };

    onCreditedAmountChange(){
        this.paymentAmount = Math.max(0, this.outstandingAmount - this.creditedAmount );
    }

    getOverpayed() {
        const amount = this.paymentAmount + this.creditedAmount - this.outstandingAmount;

        if ((amount.toFixed(2) as any) === -0) {
            return 0;
        }

        return amount;
    } 
}

@define
export class RemittanceAdviceSummary extends
    attributes({
        count : Integer,
        unmatchedCount : Integer,
        unreconciledCount : Integer,
        unresolvedCount : Integer,
        archivedCount : Integer,
        reconciledCount : Integer
    })
{
    static endpoint = restfulIO( '/api/v2/recs/summary/ra' )
}

@define
export class FileRef extends
    attributes({
        url : String,
        name : String,
    })
{
}

@define
export class EmailInfo extends
    attributes({
        attachments : [FileRef],
        to : String,
        from : String,
        sentDate : Date,
        subject : String,
        bodyHref : String,
        messageBodyHtml : String,
    })
{
}

export interface RemittanceAdvice extends AttributesMixin<typeof RemittanceAdvice> {}

@define
export class RemittanceAdvice extends Model {
    static endpoint = restfulIO( root + 'remittance-advice' );
    
    static attributes = {
        ...createdUpdatedAttrs,
        conversationId : type(Integer),
        isArchived : false,
        linkOriginType : String,
        paymentId: type(Integer).value(undefined),
        currency: type(String).required,
        referenceNumber : String,
        executionDate: type(Date),
        metadata: null, // <- arbitrary JSON
        amount : Number,
        estimatedPaymentDate: type(Date).check(function(x){ return !x || new Date(x).setHours(0, 0, 0, 0) >= new Date(this.executionDate).setHours(0, 0, 0, 0)}),
        currencyCode: String,
        instrument: String,
        creditedAmount: Number,
        processingFee: Number,
        // supplierId : type(Integer).required,
        // customerId : type(Integer).required,
        customerRelationshipId : type(String),

        matches : type([InvoiceInfo]).required,
        invoices : type([InvoiceInfo])
            .dontSave
            .endpoint( restfulIO( root + 'invoice' ) ).check((x) => Boolean(x)),

        emailMetadata : type(EmailInfo).dontSave,
        fileUrl : String,
        documentId : String,
        originalFileUrl : String,
        originalFileDetails : String,
        isReconciled : type(Boolean).dontSave,
        platformCode: String,
        originalFileName: String,
        supplierPlatformCompanyId: type(Number).required,
        customerPlatformCompanyId: type(Number).required,
    }

    get customer(){
        return globalStore.platformCompanies.get( this.customerPlatformCompanyId as any );
    }

    get supplier(){
        return globalStore.platformCompanies.get(this.supplierPlatformCompanyId as any);
    }

    get netAmount() {
        return this.totalInvoiceAmount - this.creditedAmount;
    }

    get totalInvoiceAmount() {
        return this.matches.reduce((sum: number, match) => sum + match.paymentAmount, 0)
    }

    get closestStatementDate() {
        return Math.abs((this.getOwner() as any).statementDate.getTime() - this.createdOn.getTime());
    }

    get closestValue() {
        return Math.abs((this.getOwner() as any).amount - this.amount);
    }

    get outstandingAmountFromInvoices(){
        return this.matches.reduce( ( a : number, b ) => a + b.outstandingAmount, 0 );
    }

    get creditedAmountFromInvoices(){
        return this.matches.reduce( ( a : number, b ) => a + b.creditedAmount, 0 );
    }

    get paymentAmountFromInvoices(){
        return this.matches.reduce( ( a : number, b ) => a + b.paymentAmount, 0 );
    }

    filter( text ){
        return !text
            || matchString( this.totalInvoiceAmount.toString(), text )
            || matchString( this.supplier.name, text )
            || matchString( this.customer.name, text )
            || matchString( this.referenceNumber, text )
            || this.matches.some(m => matchString( m.invoiceNumber, text ))
            || matchString( asDate(this.executionDate), text );
    }

    async save( options? : { params : { file } } ){
        const { file } = options.params;
        const url = (this.getEndpoint() as any).getUrl(this);
        const httpVerb = this.id ? 'PUT' : 'POST';

        const formData = new FormData();

        const ra = this.toJSON();
        formData.append("remittanceAdvice", JSON.stringify(ra));
        if (file) {
            formData.append("file", file, file.name);
        }

        const response = await fetch(url, {
            method: httpVerb,
            headers: new Headers({
                "Authorization": "Bearer " + getAuthToken(),
                "X-Platform-Code": getGeneralPlatformCode()
            }),
            body : formData
        });

        if(response.status < 200 || response.status >= 300){
            throw new Error(response.statusText || `HTTP Error ${response.status}`);
        }

        const json = await response.json();
        this.set(json);
        return this;
    }

    async invoiceLookup(invoiceNumber : string, supplierName : string, customerName : string ) : Promise<InvoiceAssetData[]> {

        const supplierParam = supplierName ? `&supplierName=${supplierName}` : '';
        const customerParam = customerName ? `&customerName=${customerName}` : '';
        const url =  `${root}remittance-advice/InvoiceSearch?platformCode=${this.platformCode}&invoiceNumber=${invoiceNumber}${supplierParam}${customerParam}`;
        return await fetchJson('GET', url);
    }

    get isMissingSupplierName() {
        const multipleSuppliers = this.matches.filter(m => m.unfunded && !m.id).map(m => m.invSupplierName)
            .filter((value, index, self) => self.indexOf(value) == index);
        return this.matches.some(m => !m.invSupplierName && !m.id) || (multipleSuppliers && multipleSuppliers.length > 1);
    }
}

export async function execGetRAFile(remittanceDocument : string) {
    const url = `${root}/remittance-advice/remittance-file?fileUrl=${remittanceDocument}`;

    return await fetch(url, {
        method: 'GET',
        headers: new Headers({
            "Authorization": "Bearer " + getAuthToken(),
            "X-Platform-Code": getGeneralPlatformCode()
        }),
    });
}

@define
export class RemittanceAdvicesSummary extends
    attributes({
        count : Integer,
        unresolvedCount : Integer,
        unmatchedCount : Integer,
        unreconciledCount : Integer,
        archivedCount: Integer,
        reconciledCount: Integer,
    })
{
    static endpoint = restfulIO( '/api/v2/recs/summary/ra' )
}

export async function archiveRemittanceAdvice(remittanceAdviceId: number): Promise<number> {
    const url = root + `remittance-advice/archive/${remittanceAdviceId}`;
    return await fetchJson('POST', url);
}


export class InvoiceAssetData {
    number: string;
    uid: string;
    invoiceDate: Date;
    invoiceDueDate: Date;
    faceValue: number;
    currency: string
}